import * as OTPAuth from 'otpauth';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getFirebaseDb, getFirebaseFunctions } from './firebase';

// ============================================================================
// TYPES
// ============================================================================

export interface MfaEnrollmentData {
  secret: string;
  qrCodeUrl: string;
  backupCodes: string[];
}

export interface MfaStatus {
  enabled: boolean;
  enrolledAt?: Date;
  backupCodesRemaining?: number;
}

// ============================================================================
// TOTP CONFIGURATION
// ============================================================================

const TOTP_CONFIG = {
  issuer: 'PerFin',
  algorithm: 'SHA1',
  digits: 6,
  period: 30,
} as const;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Generate a cryptographically secure random string
 */
function generateSecureRandom(length: number): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567'; // Base32 alphabet
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => chars[byte % chars.length]).join('');
}

/**
 * Generate backup codes (10 codes, 8 characters each)
 */
function generateBackupCodes(): string[] {
  const codes: string[] = [];
  for (let i = 0; i < 10; i++) {
    // Format: XXXX-XXXX for readability
    const part1 = generateSecureRandom(4);
    const part2 = generateSecureRandom(4);
    codes.push(`${part1}-${part2}`);
  }
  return codes;
}

/**
 * Hash a backup code for secure storage
 */
async function hashBackupCode(code: string): Promise<string> {
  const normalized = code.replace(/-/g, '').toUpperCase();
  const encoder = new TextEncoder();
  const data = encoder.encode(normalized);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

// ============================================================================
// MFA SERVICE FUNCTIONS
// ============================================================================

/**
 * Generate MFA enrollment data (secret, QR code URL, backup codes)
 * Call this when user initiates MFA setup
 */
export function generateMfaEnrollment(email: string): MfaEnrollmentData {
  // Generate a new TOTP secret
  const secret = new OTPAuth.Secret({ size: 20 });

  // Create TOTP instance
  const totp = new OTPAuth.TOTP({
    issuer: TOTP_CONFIG.issuer,
    label: email,
    algorithm: TOTP_CONFIG.algorithm,
    digits: TOTP_CONFIG.digits,
    period: TOTP_CONFIG.period,
    secret: secret,
  });

  // Generate QR code URL (otpauth:// URI)
  const qrCodeUrl = totp.toString();

  // Generate backup codes
  const backupCodes = generateBackupCodes();

  return {
    secret: secret.base32,
    qrCodeUrl,
    backupCodes,
  };
}

/**
 * Verify a TOTP code against a secret
 * Returns true if code is valid (allows 1 period window for clock drift)
 */
export function verifyTotpCode(secret: string, code: string): boolean {
  try {
    const totp = new OTPAuth.TOTP({
      issuer: TOTP_CONFIG.issuer,
      algorithm: TOTP_CONFIG.algorithm,
      digits: TOTP_CONFIG.digits,
      period: TOTP_CONFIG.period,
      secret: OTPAuth.Secret.fromBase32(secret),
    });

    // Validate with a window of 1 (allows previous and next period)
    const delta = totp.validate({ token: code, window: 1 });
    return delta !== null;
  } catch {
    return false;
  }
}

/**
 * Enable MFA for a user after successful code verification
 * Uses Cloud Function to bypass Firestore rules (user doesn't have mfaVerified yet)
 * Server-side: verifies TOTP, hashes backup codes, writes to Firestore, sets claims
 */
export async function enableMfa(
  _userId: string,
  secret: string,
  backupCodes: string[],
  verifyCode: string
): Promise<void> {
  const functions = getFirebaseFunctions();
  const enrollMfaFn = httpsCallable<
    { secret: string; backupCodes: string[]; verifyCode: string },
    { success: boolean }
  >(functions, 'enrollMfaFunction');

  await enrollMfaFn({ secret, backupCodes, verifyCode });
}

/**
 * Disable MFA for a user
 * Uses Cloud Function to clear Firestore data and custom claims
 */
export async function disableMfa(_userId: string): Promise<void> {
  const functions = getFirebaseFunctions();
  const disableMfaFn = httpsCallable<
    Record<string, never>,
    { success: boolean }
  >(functions, 'disableMfaFunction');

  await disableMfaFn({});
}

/**
 * Get MFA status for a user
 */
export async function getMfaStatus(userId: string): Promise<MfaStatus> {
  const db = getFirebaseDb();
  const userRef = doc(db, 'users', userId);
  const userDoc = await getDoc(userRef);

  if (!userDoc.exists()) {
    return { enabled: false };
  }

  const data = userDoc.data();
  return {
    enabled: data.mfaEnabled === true,
    enrolledAt: data.mfaEnrolledAt?.toDate(),
    backupCodesRemaining: data.mfaBackupCodesRemaining,
  };
}

/**
 * Get MFA secret for verification (internal use during login)
 */
export async function getMfaSecret(userId: string): Promise<string | null> {
  const db = getFirebaseDb();
  const userRef = doc(db, 'users', userId);
  const userDoc = await getDoc(userRef);

  if (!userDoc.exists()) {
    return null;
  }

  return userDoc.data().mfaSecret || null;
}

/**
 * Verify MFA code server-side via Cloud Function
 * This is the secure method that sets the mfaVerified claim
 */
export async function verifyMfaCodeServerSide(
  code: string
): Promise<{ success: boolean; usedBackupCode: boolean }> {
  const functions = getFirebaseFunctions();
  const verifyMfaCodeFn = httpsCallable<
    { code: string },
    { success: boolean; usedBackupCode: boolean }
  >(functions, 'verifyMfaCodeFunction');

  const result = await verifyMfaCodeFn({ code });
  return result.data;
}

/**
 * Clear mfaVerified claim on login - forces MFA re-verification each session
 * Returns whether MFA is required and if setup is needed (mandatory MFA)
 */
export async function clearMfaVerifiedOnLogin(): Promise<{ mfaRequired: boolean; mfaSetupRequired: boolean }> {
  const functions = getFirebaseFunctions();
  const clearMfaVerifiedFn = httpsCallable<
    Record<string, never>,
    { mfaRequired: boolean; mfaSetupRequired: boolean }
  >(functions, 'clearMfaVerified');

  const result = await clearMfaVerifiedFn({});
  return result.data;
}

/**
 * Generate new backup codes (invalidates old ones)
 */
export async function regenerateBackupCodes(userId: string): Promise<string[]> {
  const db = getFirebaseDb();
  const userRef = doc(db, 'users', userId);

  const newCodes = generateBackupCodes();
  const hashedCodes = await Promise.all(newCodes.map(hashBackupCode));

  await updateDoc(userRef, {
    mfaBackupCodes: hashedCodes,
    mfaBackupCodesRemaining: hashedCodes.length,
    updatedAt: serverTimestamp(),
  });

  return newCodes;
}

/**
 * Set MFA skip flag for a user (superAdmin only)
 * When true, user bypasses MFA challenge on login but enrollment stays intact.
 */
export async function setMfaSkip(userId: string, skip: boolean): Promise<void> {
  const functions = getFirebaseFunctions();
  const setMfaSkipFn = httpsCallable<
    { userId: string; skip: boolean },
    { success: boolean }
  >(functions, 'setMfaSkipFunction');
  await setMfaSkipFn({ userId, skip });
}

/**
 * Get MFA skip status for all users in an organization
 */
export async function getMfaSkipStatus(userIds: string[]): Promise<Record<string, boolean>> {
  const db = getFirebaseDb();
  const result: Record<string, boolean> = {};
  for (const userId of userIds) {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      result[userId] = userDoc.data()?.mfaSkip === true;
    } else {
      result[userId] = false;
    }
  }
  return result;
}
